// AlertManager.js — DROP-IN (Type required when types exist; default single type)

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import trashIcon from "./Assets/trash.svg";

import {
    API,
    listAlertVehicles,
    addAlertVehicle,
    removeAlertVehicle,
    getAlertTypesSnapshot,
    refreshAlertTypes,
    listAlertTypes,
    createAlertType,
    updateAlertType,
    deleteAlertType,
    prettyColourHex,
    updateAlertVehicle, // <<< use shared helper (credentials + PUT)
} from "./data";

/* ---------------------------------- Row ---------------------------------- */
function Row({ r, onRemove, showType, onStartEdit }) {
    const bandHex = prettyColourHex(r.colour);

    return (
        <tr className="align-middle">
            {/* VRM */}
            <td className="text-center">
                <span className="registration-ui-front-small">{r.vrm}</span>
            </td>

            {/* MAKE (slightly wider + truncate) */}
            <td className="vehicle-cell align-items-center" style={{ maxWidth: 200 }}>
                <div className="vehicle-logo-wrapper">
                    <span className="vehicle-logo" aria-hidden="true" />
                </div>
                <div className="vehicle-lines" style={{ minWidth: 0 }}>
                    <div className="vehicle-topline">
                        <div
                            className="vehicle-make fw-normal text-nowrap text-truncate"
                            title={r.make || ""}
                            style={{ maxWidth: 180 }}
                        >
                            {r.make || "—"}
                        </div>
                    </div>
                </div>
            </td>

            {/* VEHICLE COLOUR (always true vehicle colour) */}
            <td className="text-center">
                <div className="vehicle-right">
                    <div
                        className="vehicle-colourcircle"
                        style={{ backgroundColor: bandHex || "#ccc" }}
                        aria-label={r.colour || "colour"}
                        title={r.colour || "colour"}
                    />
                    {r.colour && <div className="vehicle-colourlabel">{r.colour}</div>}
                </div>
            </td>

            {/* TYPE */}
            {showType && (
                <td className="text-center">
                    {r.type_label ? (
                        <span
                            className="badge rounded-pill"
                            style={{
                                backgroundColor: r.type_color_hex || "#444",
                                color: "#fff",
                                fontWeight: 700,
                            }}
                            title={r.type_label}
                        >
                            {r.type_label}
                        </span>
                    ) : (
                        "—"
                    )}
                </td>
            )}

            {/* NOTE */}
            <td className="text-start">
                <div className="text-truncate" title={r.note || ""}>
                    {r.note || "—"}
                </div>
            </td>

            {/* ACTIONS */}
            <td className="text-end">
                <div className="d-inline-flex gap-2 flex-wrap" style={{ justifyContent: "flex-end" }}>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onStartEdit(r)}>
                        Edit
                    </button>
                    <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        aria-label={`Remove ${r.vrm}`}
                        title="Remove"
                        onClick={() => onRemove(r.vrm)}
                    >
                        <img src={trashIcon} alt="Delete" style={{ width: 24, height: 24 }} />
                    </button>
                </div>
            </td>
        </tr>
    );
}

/* ------------------------- Tiny circular swatch UI ------------------------ */
const PALETTE = ["#E74C3C", "#F39C12", "#F1C40F", "#2ECC71", "#3498DB", "#9B59B6"];
function SwatchPicker({ value, onChange, size = 28 }) {
    return (
        <div className="d-flex flex-wrap gap-2">
            {PALETTE.map((hex) => {
                const selected = String(value).toLowerCase() === hex.toLowerCase();
                return (
                    <button
                        key={hex}
                        type="button"
                        onClick={() => onChange(hex)}
                        title={hex}
                        aria-label={hex}
                        className="btn p-0"
                        style={{
                            width: size,
                            height: size,
                            borderRadius: "50%",
                            background: hex,
                            border: selected ? "2px solid rgba(0,0,0,.25)" : "1px solid rgba(0,0,0,.12)",
                            boxShadow: "0 0 0 1px rgba(0,0,0,.06)",
                        }}
                    />
                );
            })}
        </div>
    );
}

/* ------------------------- Type Manager (portal modal) -------------------- */
function TypeManager({ open, onClose }) {
    const [busy, setBusy] = useState(false);
    const [rows, setRows] = useState([]);
    const [label, setLabel] = useState("");
    const [color, setColor] = useState(PALETTE[0]);
    const [editingKey, setEditingKey] = useState(null);
    const [editLabel, setEditLabel] = useState("");
    const [editColor, setEditColor] = useState(PALETTE[0]);

    const load = async () => {
        try {
            const ts = await listAlertTypes();
            setRows(ts || []);
        } catch {
            setRows([]);
        }
    };

    useEffect(() => {
        if (!open) return;
        setBusy(false);
        setLabel("");
        setColor(PALETTE[0]);
        setEditingKey(null);
        load();
    }, [open]);

    const canCreate = label.trim().length >= 1;

    const handleCreate = async () => {
        if (!canCreate) return;
        setBusy(true);
        try {
            await createAlertType({
                key: label.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, ""),
                label: label.trim(),
                color_hex: color,
            });
            await refreshAlertTypes({ force: true });
            await load();
            setLabel("");
            setColor(PALETTE[0]);
        } catch (e) {
            alert(e?.message || "Failed to create type");
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (t) => {
        setEditingKey(t.key);
        setEditLabel(t.label);
        setEditColor(t.color_hex || PALETTE[0]);
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditLabel("");
        setEditColor(PALETTE[0]);
    };

    const saveEdit = async () => {
        if (!editingKey) return;
        if (editLabel.trim().length < 2) {
            alert("Please enter a label (min 2 chars).");
            return;
        }
        setBusy(true);
        try {
            await updateAlertType(editingKey, { label: editLabel.trim(), color_hex: editColor });
            await refreshAlertTypes({ force: true });
            await load();
            cancelEdit();
        } catch (e) {
            alert(e?.message || "Failed to update type");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (key, label) => {
        if (!window.confirm(`Delete type “${label}”? Alerts of this type will be reassigned (or cleared) automatically.`)) return;
        setBusy(true);
        try {
            await deleteAlertType(key);
            await refreshAlertTypes({ force: true });
            await load();
        } catch (e) {
            alert(e?.message || "Failed to delete type");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1064 }} onClick={onClose} />
            <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-label="Manage Alert Types" style={{ zIndex: 1065 }}>
                <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: 860 }}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <strong>Alert Types</strong>
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} aria-label="Close">
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            {/* Create row */}
                            <div className="row g-2 align-items-end mb-3">
                                <div className="col-12 col-md-6">
                                    <label className="form-label">Name</label>
                                    <input
                                        className="form-control"
                                        value={label}
                                        onChange={(e) => setLabel(e.target.value)}
                                        placeholder="e.g. VIP, Barred, WatchList"
                                        style={{ height: 38 }}
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <label className="form-label">Colour</label>
                                    <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
                                        <SwatchPicker value={color} onChange={setColor} />
                                    </div>
                                </div>

                                <div className="col-12 col-md-2 d-flex">
                                    <button
                                        className="btn btn-dark w-100 ms-md-auto"
                                        disabled={!canCreate || busy}
                                        onClick={handleCreate}
                                        style={{ height: 38 }}
                                    >
                                        {busy ? "Saving…" : "Add"}
                                    </button>
                                </div>
                            </div>

                            {/* List */}
                            {rows.length === 0 ? (
                                <div className="text-muted small">No types yet. Create your first type above.</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle">
                                        <thead className="small text-uppercase text-muted">
                                            <tr>
                                                <th>Name</th>
                                                <th>Colour</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((t) => {
                                                const editing = editingKey === t.key;
                                                return (
                                                    <tr key={t.key}>
                                                        <td>
                                                            {editing ? (
                                                                <input
                                                                    className="form-control form-control-sm"
                                                                    value={editLabel}
                                                                    onChange={(e) => setEditLabel(e.target.value)}
                                                                    style={{ height: 32 }}
                                                                />
                                                            ) : (
                                                                t.label
                                                            )}
                                                        </td>

                                                        <td>
                                                            {editing ? (
                                                                <SwatchPicker value={editColor} onChange={setEditColor} size={32} />
                                                            ) : (
                                                                <span
                                                                    aria-label={t.color_hex}
                                                                    title={t.color_hex}
                                                                    style={{
                                                                        display: "inline-block",
                                                                        width: 24,
                                                                        height: 24,
                                                                        borderRadius: "50%",
                                                                        background: t.color_hex,
                                                                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,.12)",
                                                                    }}
                                                                />
                                                            )}
                                                        </td>

                                                        <td className="text-end">
                                                            {editing ? (
                                                                <div className="d-inline-flex gap-2 flex-wrap" style={{ justifyContent: "flex-end" }}>
                                                                    <button className="btn btn-sm btn-dark" disabled={busy} onClick={saveEdit}>
                                                                        Save
                                                                    </button>
                                                                    <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={cancelEdit}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="d-inline-flex gap-2">
                                                                    <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => startEdit(t)}>
                                                                        Edit
                                                                    </button>
                                                                    <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => remove(t.key, t.label)}>
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

function InlineEditor({ row, types, showTypeColumn, onCancel, onSave }) {
    const hasTypes = showTypeColumn && Array.isArray(types) && types.length >= 1;
    const [note, setNote] = useState(row?.note || "");
    const [typeKey, setTypeKey] = useState(row?.type_key || "");

    // When entering edit: if exactly one type and none set, preselect it
    useEffect(() => {
        if (hasTypes && !row?.type_key && types.length === 1) {
            setTypeKey(types[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasTypes]);

    const canSave = !hasTypes || !!typeKey; // require a type if any exist

    return (
        <tr className="bg-light">
            {/* VRM */}
            <td className="text-center">
                <span className="registration-ui-front-small">{row.vrm}</span>
            </td>

            {/* Make placeholder */}
            <td />

            {/* Vehicle colour placeholder */}
            <td />

            {/* Type cell */}
            {showTypeColumn && (
                <td className="text-center">
                    <div className="d-flex flex-column align-items-center">
                        <select
                            className="form-select form-select-sm"
                            value={typeKey || ""}
                            onChange={(e) => setTypeKey(e.target.value)}
                            style={{ width: 120, display: "inline-block" }}
                        >
                            {hasTypes ? (
                                <>
                                    <option value="">Select type…</option>
                                    {types.map((t) => (
                                        <option key={t.key} value={t.key}>
                                            {t.label}
                                        </option>
                                    ))}
                                </>
                            ) : (
                                <option value="">(none)</option>
                            )}
                        </select>
                    </div>
                </td>
            )}

            {/* Note */}
            <td>
                <input
                    className="form-control form-control-sm"
                    placeholder="Note…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </td>

            {/* Actions */}
            <td className="text-end" style={{ maxWidth: 160 }}>
                <div className="d-inline-flex gap-2 flex-wrap" style={{ justifyContent: "flex-end" }}>
                    <button
                        type="button"
                        className="btn btn-sm btn-dark"
                        disabled={!canSave}
                        onClick={() => onSave({ note: note.trim(), typeKey: hasTypes ? typeKey : null })}
                    >
                        Save
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </td>
        </tr>
    );
}

/* ----------------------------- Alert Manager ------------------------------ */
export default function AlertManager({ onClose }) {
    const PAGE_SIZE = 10;
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [busy, setBusy] = useState(false);
    const [vrmToAdd, setVrmToAdd] = useState("");
    const [note, setNote] = useState("");

    const [types, setTypes] = useState(getAlertTypesSnapshot());
    const showTypeColumn = types.length >= 1; // if no types, unchanged (hidden)
    const [selectedTypeKey, setSelectedTypeKey] = useState("");

    const [typesOpen, setTypesOpen] = useState(false);
    const [editingVrm, setEditingVrm] = useState(null);

    async function refresh(p = page, query = q) {
        setBusy(true);
        try {
            let res = await listAlertVehicles(query, p, PAGE_SIZE);
            if (res.pages > 0 && res.page > res.pages) {
                res = await listAlertVehicles(query, res.pages, PAGE_SIZE);
            }
            setRows(res.rows || []);
            setPages(res.pages || 1);
            setTotal(res.total || 0);
            setPage(res.page || 1);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        refresh(1, q);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // keep types fresh + default single type
    useEffect(() => {
        let ok = true;
        refreshAlertTypes({ force: true }).then((ts) => {
            if (!ok) return;
            setTypes(ts);
            if (Array.isArray(ts) && ts.length === 1) {
                setSelectedTypeKey(ts[0].key); // auto-select the only type
            }
        });
        const onTypes = () => {
            const snap = getAlertTypesSnapshot();
            setTypes(snap);
            if (snap.length === 1) setSelectedTypeKey(snap[0].key);
            if (snap.length === 0) setSelectedTypeKey("");
        };
        window.addEventListener("alerttypes-updated", onTypes);

        const onAlerts = () => refresh(page, q);
        window.addEventListener("alertlist-updated", onAlerts);

        return () => {
            ok = false;
            window.removeEventListener("alerttypes-updated", onTypes);
            window.removeEventListener("alertlist-updated", onAlerts);
        };
    }, [page, q]);

    const canAdd =
        vrmToAdd.trim().length >= 2 &&
        (!showTypeColumn || selectedTypeKey); // when types exist, a type is required

    async function handleAdd() {
        if (!canAdd) return;
        setBusy(true);
        try {
            const payload = { note: note.trim() || undefined };
            if (showTypeColumn) payload.typeKey = selectedTypeKey; // mandatory when visible
            await addAlertVehicle(vrmToAdd.trim().toUpperCase(), payload);
            setVrmToAdd("");
            setNote("");
            setSelectedTypeKey(types.length === 1 ? types[0].key : ""); // keep single-type default
            await refresh(1, q);
        } finally {
            setBusy(false);
        }
    }

    async function handleRemove(vrm) {
        if (!window.confirm(`Remove ${vrm} from Alert List?`)) return;
        setBusy(true);
        try {
            await removeAlertVehicle(vrm);
            await refresh(page, q);
        } finally {
            setBusy(false);
        }
    }

    const typeSelect = useMemo(() => {
        if (!showTypeColumn) return null;
        return (
            <div className="col-auto">
                <div className="d-flex flex-column">
                    <select
                        className="form-select"
                        value={selectedTypeKey}
                        onChange={(e) => setSelectedTypeKey(e.target.value)}
                        style={{ width: 200 }}
                    >
                        <option value="">Select type…</option>
                        {types.map((t) => (
                            <option key={t.key} value={t.key}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }, [showTypeColumn, selectedTypeKey, types]);

    // NEW: close on overlay click (outside the panel) + Esc
    const handleOverlayPointerDown = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    return (
        <>
            <div className="modal-backdrop fade show" onClick={onClose} />
            <div
                className="modal show d-block"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                aria-label="Manage Alert List"
                onPointerDown={handleOverlayPointerDown}
                onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
                style={{ pointerEvents: "auto" }}
            >
                <div
                    className="modal-dialog modal-dialog-centered alert-manager-modal"
                    style={{ maxWidth: "min(1040px, calc(100vw - 24px))" }}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <span className="alert-title">Alert List</span>
                                <span className="text-muted small">{total} total</span>
                            </div>

                            <div className="d-flex gap-2 ms-auto flex-wrap">
                                <span className="alert-vrm-search-input" style={{ flex: "0 0 auto" }}>
                                    <input
                                        className="form-control"
                                        placeholder="Search VRM / make / colour / note"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                refresh(1, q);
                                            }
                                        }}
                                        style={{ width: 340, maxWidth: "100%" }}
                                    />
                                </span>
                                <button type="button" className="btn btn-outline-secondary" onClick={() => refresh(1, q)}>
                                    Search
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setQ("");
                                        refresh(1, "");
                                    }}
                                >
                                    Clear
                                </button>
                                <button type="button" className="btn btn-outline-secondary" onClick={onClose} aria-label="Close">
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Add row */}
                        <div className="border-top p-2">
                            <div className="row g-2 align-items-center flex-nowrap" style={{ overflowX: "auto" }}>
                                <div className="col-auto">
                                    <input
                                        className="form-control"
                                        placeholder="Add VRM…"
                                        value={vrmToAdd}
                                        onChange={(e) => setVrmToAdd(e.target.value.toUpperCase())}
                                        style={{ width: 230 }}
                                    />
                                </div>

                                {typeSelect}

                                <div className="col">
                                    <input
                                        className="form-control"
                                        placeholder="Optional note…"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        style={{ maxWidth: 400 }}
                                    />
                                </div>
                                <div className="col-auto">
                                    <button type="button" disabled={!canAdd || busy} className="btn btn-dark w-100" onClick={handleAdd}>
                                        {busy ? "Working…" : "Add"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="modal-body pt-2">
                            {rows.length === 0 ? (
                                <div className="table-empty-cell">
                                    <div className="empty-icon">☰</div>
                                    <div className="empty-title">No items</div>
                                    <div className="empty-subtitle">Try adjusting your search or add a VRM.</div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm align-middle mb-0 alert-table" style={{ width: "100%" }}>
                                        <colgroup>
                                            <col style={{ width: 140 }} />
                                            <col style={{ width: 200 }} />
                                            <col style={{ width: 110 }} />
                                            {showTypeColumn && <col style={{ width: 120 }} />}
                                            <col />
                                            <col style={{ width: 160 }} />
                                        </colgroup>

                                        <thead className="small text-uppercase text-muted">
                                            <tr>
                                                <th className="text-center th-vrm">VRM</th>
                                                <th className="text-center">Make</th>
                                                <th className="text-center">Colour</th>
                                                {showTypeColumn && <th className="text-center">Type</th>}
                                                <th className="text-center">Note</th>
                                                <th className="text-end th-actions" aria-label="Actions"></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {rows.map((r) =>
                                                editingVrm === r.vrm ? (
                                                    <InlineEditor
                                                        key={`${r.vrm}-edit`}
                                                        row={r}
                                                        types={types}
                                                        showTypeColumn={showTypeColumn}
                                                        onCancel={() => setEditingVrm(null)}
                                                        onSave={async ({ note, typeKey }) => {
                                                            try {
                                                                await updateAlertVehicle(r.vrm, { note, typeKey });
                                                                setEditingVrm(null);
                                                                await refresh(page, q);
                                                            } catch (e) {
                                                                alert(e?.message || "Update failed");
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Row
                                                        key={r.vrm}
                                                        r={r}
                                                        showType={showTypeColumn}
                                                        onRemove={handleRemove}
                                                        onStartEdit={() => setEditingVrm(r.vrm)}
                                                    />
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="modal-footer d-flex align-items-center">
                            <div className="me-auto">Page {page} of {pages}</div>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setTypesOpen(true)}
                                    title="Create, edit or delete alert types"
                                >
                                    Manage Types
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    disabled={page <= 1 || busy}
                                    onClick={() => {
                                        const p = Math.max(1, page - 1);
                                        setPage(p);
                                        refresh(p, q);
                                    }}
                                >
                                    ‹ Prev
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    disabled={page >= pages || busy}
                                    onClick={() => {
                                        const p = Math.min(pages, page + 1);
                                        setPage(p);
                                        refresh(p, q);
                                    }}
                                >
                                    Next ›
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Type manager modal */}
            <TypeManager
                open={typesOpen}
                onClose={() => {
                    setTypesOpen(false);
                    refresh(page, q);
                }}
            />
        </>
    );
}
